// @ts-nocheck
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useEffect } from "react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Img from "@insite/mobius/Img";
import { ImagePresentationProps } from "@store/MobiusOverrides/Img/Img";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useProductState } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { LoadProductParameter } from "@store/Extensions/Store/Data/Products/Handlers/LoadProduct";

export interface ReferenceProductPresentationProps extends ExpandStyles<ReferenceProductStyles> {}

export interface ReferenceProductProps extends ReferenceProductPresentationProps {
    productId: string | undefined;
}

export interface ReferenceProductStyles {
    contentWrapper?: InjectableCss;
    image?: ImagePresentationProps;
    imageWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    productTitle?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

const referenceProductStyles: ReferenceProductStyles = {
    wrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(8)};
        `,
    },
    imageWrapper: {
        css: css`
            flex-shrink: 0;
            width: ${pxToRem(48)};
            min-height: ${pxToRem(48)};
        `,
    },
    image: {
        css: css`
            max-width: 100%;
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    productPartNumbers: {
        wrapper: {
            css: css`
                ${spacingStyles(null, pxToRem(3))}
                color: ${getColor("text.light")};
                margin-top: ${pxToRem(1)};
            `,
        },
        innerWrapper: {
            css: css`
                ${inlineSeparatorStyles({ spacing: "1em", color: "text.accent" })}
                margin-bottom: 0;
            `,
        },
    },
    productTitle: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
};

const ReferenceProduct: FC<ReferenceProductProps> = ({ extendedStyles, overridedStyles, productId }) => {
    const dispatch = useAppDispatch();
    const { isLoading, value: product } = useProductState(productId);
    const styles = useExpandProps(referenceProductStyles, extendedStyles, overridedStyles);

    useEffect(() => {
        if (productId && !isLoading && !product) {
            dispatch(
                loadProduct({
                    handleNotFoundError: true,
                    id: productId,
                } as LoadProductParameter),
            );
        }
    }, [productId]);

    if (!productId || !product || product.isNotFound) {
        return null;
    }

    const {
        customerProductNumber,
        productNumber,
        productTitle,
        properties: { smallImagePath },
    } = product;

    return (
        <StyledWrapper {...styles.wrapper}>
            {Boolean(smallImagePath) && (
                <StyledWrapper {...styles.imageWrapper}>
                    <Img {...styles.image} alt={productTitle} loading="lazy" src={smallImagePath} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.contentWrapper}>
                <ProductPartNumbers
                    customerProductNumber={customerProductNumber}
                    extendedStyles={styles.productPartNumbers}
                    productNumber={productNumber}
                />
                <Typography {...styles.productTitle}>{productTitle}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ReferenceProduct;
